(function() {
  if (customElements.get('global-product-purchase-bar')) {
    return;
  }

  class GlobalProductPurchaseBar extends HTMLElement {
    constructor() {
      super();

      this.onQuantityClick = this.onQuantityClick.bind(this);
      this.onQuantityInput = this.onQuantityInput.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onBuyNow = this.onBuyNow.bind(this);
      this.onAjaxProductAdded = this.onAjaxProductAdded.bind(this);
      this.onAjaxProductError = this.onAjaxProductError.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.body.classList.add('global-purchase-bar-active');

      this.quantityInput = this.querySelector('[data-global-purchase-quantity]');
      this.errorElement = this.querySelector('[data-global-purchase-error]');
      this.submitButton = this.querySelector('[data-global-purchase-submit]');
      this.buyNowButton = this.querySelector('[data-global-purchase-buy-now]');
      this.variantElement = this.querySelector('[data-global-product-variant]');
      this.priceElement = this.querySelector('[data-global-product-price]');
      this.priceTextElement = this.querySelector('[data-global-product-price-text]');
      this.savingsElement = this.querySelector('[data-global-product-savings]');
      this.mediaElement = this.querySelector('[data-global-product-media-wrapper] img');
      this.form = this.findProductForm();

      this.bindProductForm();
      this.syncQuantityFromForm();

      this.addEventListener('click', this.onQuantityClick);
      this.quantityInput?.addEventListener('input', this.onQuantityInput);
      this.buyNowButton?.addEventListener('click', this.onBuyNow);
      document.addEventListener('ajaxProduct:added', this.onAjaxProductAdded);
      document.addEventListener('ajaxProduct:error', this.onAjaxProductError);

      this.subscribeToVariantChanges();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.form?.removeEventListener('variant:change', this.onVariantChanged);
      this.removeEventListener('click', this.onQuantityClick);
      this.quantityInput?.removeEventListener('input', this.onQuantityInput);
      this.buyNowButton?.removeEventListener('click', this.onBuyNow);
      document.removeEventListener('ajaxProduct:added', this.onAjaxProductAdded);
      document.removeEventListener('ajaxProduct:error', this.onAjaxProductError);
      this.variantChangeUnsubscriber?.();

      if (!document.querySelector('global-product-purchase-bar')) {
        document.body.classList.remove('global-purchase-bar-active');
      }
    }

    findProductForm() {
      return document.querySelector('product-info form[is="product-form"][data-type="add-to-cart-form"]')
        || document.querySelector('main form[is="product-form"][data-type="add-to-cart-form"]')
        || document.querySelector('form[is="product-form"][data-type="add-to-cart-form"]')
        || document.querySelector('main form[action*="/cart/add"]');
    }

    bindProductForm() {
      if (!this.form) {
        this.toggleButtons(true, this.dataset.unavailableText);
        return;
      }

      if (!this.form.id) {
        this.form.id = `ProductForm-${this.dataset.productId}`;
      }

      this.querySelectorAll('[data-global-purchase-submit]').forEach((button) => {
        button.setAttribute('form', this.form.id);
      });

      this.form.addEventListener('submit', this.onFormSubmit, true);
      this.form.addEventListener('variant:change', this.onVariantChanged);
    }

    get formQuantityInput() {
      if (!this.form) return null;
      let input = this.form.querySelector('input[name="quantity"]') || document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = this.quantityInput?.value || '1';
        this.form.appendChild(input);
      }

      return input;
    }

    syncQuantityFromForm() {
      if (!this.quantityInput) return;

      const formQuantityInput = this.formQuantityInput;
      if (!formQuantityInput) return;

      ['min', 'max', 'step'].forEach((attribute) => {
        if (formQuantityInput.hasAttribute(attribute)) {
          this.quantityInput.setAttribute(attribute, formQuantityInput.getAttribute(attribute));
        }
      });

      this.quantityInput.value = formQuantityInput.value || this.quantityInput.value || this.quantityInput.min || '1';
    }

    syncQuantityToForm() {
      const formQuantityInput = this.formQuantityInput;
      if (!formQuantityInput || !this.quantityInput) return;

      formQuantityInput.value = this.normalizeQuantity(this.quantityInput.value);
      formQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    normalizeQuantity(value) {
      const min = Number(this.quantityInput?.getAttribute('min')) || 1;
      const max = Number(this.quantityInput?.getAttribute('max')) || Infinity;
      const step = Number(this.quantityInput?.getAttribute('step')) || 1;
      let quantity = Number.parseInt(value, 10);

      if (Number.isNaN(quantity)) quantity = min;
      quantity = Math.max(min, Math.min(max, quantity));

      if (step > 1) {
        quantity = min + Math.round((quantity - min) / step) * step;
        quantity = Math.max(min, Math.min(max, quantity));
      }

      return String(quantity);
    }

    onQuantityClick(event) {
      const button = event.target.closest('[data-global-purchase-quantity-action]');
      if (!button || !this.quantityInput) return;

      const action = button.getAttribute('data-global-purchase-quantity-action');
      const step = Number(this.quantityInput.getAttribute('step')) || 1;
      const currentQuantity = Number.parseInt(this.quantityInput.value, 10) || Number(this.quantityInput.min) || 1;
      const nextQuantity = action === 'increase' ? currentQuantity + step : currentQuantity - step;

      this.quantityInput.value = this.normalizeQuantity(nextQuantity);
      this.syncQuantityToForm();
    }

    onQuantityInput() {
      if (!this.quantityInput) return;

      this.quantityInput.value = this.normalizeQuantity(this.quantityInput.value);
      this.syncQuantityToForm();
    }

    onFormSubmit() {
      this.syncQuantityToForm();
      this.hideError();
    }

    async onBuyNow() {
      if (!this.form) return;

      this.syncQuantityToForm();
      this.hideError();
      this.setBusy(true);

      try {
        const formData = new FormData(this.form);
        if (this.quantityInput) {
          formData.set('quantity', this.normalizeQuantity(this.quantityInput.value));
        }

        const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const parsedState = await response.json();

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Cannot add product to cart');
        }

        window.location.href = this.dataset.checkoutUrl || '/checkout';
      } catch (error) {
        this.showError(error.message);
        this.setBusy(false);
      }
    }

    onAjaxProductAdded() {
      this.hideError();
    }

    onAjaxProductError(event) {
      this.showError(event.detail?.errorMessage);
    }

    subscribeToVariantChanges() {
      const pubsub = window.theme?.pubsub;
      if (!pubsub?.subscribe || !pubsub.PUB_SUB_EVENTS?.variantChange) return;

      this.variantChangeUnsubscriber = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        this.updateForVariant(data?.variant, data?.parsedHTML, data?.sectionId, data?.productId);
      });
    }

    onVariantChanged(event) {
      this.updateForVariant(event.detail?.variant);
    }

    updateForVariant(variant, parsedHTML, sectionId, productId) {
      if (!variant) {
        this.toggleButtons(true, this.dataset.unavailableText);
        return;
      }

      this.dataset.currentVariantId = variant.id;
      this.updateVariantText(variant);
      this.updateMedia(variant);
      this.updatePrice(variant, parsedHTML, sectionId, productId);
      this.updateSavings(variant);
      this.toggleButtons(!variant.available, variant.available ? this.currentSubmitText() : this.dataset.soldOutText);
      this.syncQuantityFromForm();
    }

    updateVariantText(variant) {
      if (!this.variantElement) return;
      this.variantElement.textContent = variant.title || '';
      this.variantElement.hidden = !variant.title || variant.title === 'Default Title';
    }

    updateMedia(variant) {
      const previewImage = variant.featured_media?.preview_image;
      if (!this.mediaElement || !previewImage) return;

      this.mediaElement.src = previewImage.src;
      this.mediaElement.alt = variant.featured_media.alt || '';
      if (previewImage.width) {
        const widths = [56, 112, 160].filter((width) => width <= previewImage.width);
        this.mediaElement.srcset = widths.map((width) => `${previewImage.src}&width=${width} ${width}w`).join(', ');
      }
    }

    updatePrice(variant, parsedHTML, sectionId, productId) {
      if (variant?.price != null && this.priceTextElement) {
        this.priceTextElement.textContent = this.formatMoney(variant.price);
        this.priceElement?.removeAttribute('hidden');
        return;
      }

      if (!parsedHTML || !this.priceElement || !sectionId || !productId) return;

      const source = parsedHTML.getElementById(`Price-${sectionId}-${productId}`)
        || parsedHTML.getElementById(`StickyPrice-${sectionId}-${productId}`)
        || parsedHTML.getElementById(`BuyButtonPrice-${sectionId}-${productId}`);

      if (!source) return;

      this.priceElement.innerHTML = source.innerHTML;
      this.priceElement.removeAttribute('hidden');
    }

    updateSavings(variant) {
      if (!this.savingsElement) return;

      const compareAtPrice = Number(variant?.compare_at_price) || 0;
      const price = Number(variant?.price) || 0;
      const savings = compareAtPrice - price;

      if (savings > 0) {
        this.savingsElement.textContent = `Save ${this.formatMoney(savings)}`;
        this.savingsElement.hidden = false;
      } else {
        this.savingsElement.textContent = '';
        this.savingsElement.hidden = true;
      }
    }

    formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      const locale = this.dataset.moneyLocale || document.documentElement.lang || 'en';
      const currency = this.dataset.moneyCurrency || window.Shopify?.currency?.active || 'USD';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
      }
    }

    currentSubmitText() {
      return this.submitButton?.hasAttribute('data-pre-order') ? this.dataset.preOrderText : this.dataset.addToCartText;
    }

    toggleButtons(disable, text) {
      [this.submitButton, this.buyNowButton].forEach((button) => {
        if (!button) return;
        button.toggleAttribute('disabled', disable);
      });

      const submitText = this.querySelector('[data-global-purchase-submit-text]');
      if (submitText && text) {
        submitText.textContent = text;
      }
    }

    setBusy(isBusy) {
      [this.submitButton, this.buyNowButton].forEach((button) => {
        if (!button) return;
        button.toggleAttribute('aria-busy', isBusy);
      });
    }

    showError(message) {
      if (!this.errorElement || !message) return;

      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    hideError() {
      if (!this.errorElement) return;

      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }
  }

  customElements.define('global-product-purchase-bar', GlobalProductPurchaseBar);
})();
